import React, { useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import Button from '../Button'
import Input from '../Input'
import TextArea from '../TextArea'
import Alert from '../Alert'
import { Link } from 'gatsby'

interface ContactForm {
  show: boolean
  onClose: () => void
}

export default function ContactForm({ show, onClose }: ContactForm) {
  const [isMounted, setIsMounted] = useState(false)
  const [status, setStatus] = useState<
    'success' | 'error' | 'incomplete' | 'loading' | ''
  >('')
  const [{ name, lastName, email, message }, setForm] = useState({
    name: '',
    lastName: '',
    email: '',
    message: ''
  })

  useEffect(() => {
    if (show) {
      const timeout = setTimeout(() => {
        setIsMounted(true)
      }, 0)
      return () => clearTimeout(timeout)
    }
  }, [show])

  useEffect(() => {
    if (!isMounted && show) {
      const timeout = setTimeout(() => {
        onClose()
      }, 300)
      return () => clearTimeout(timeout)
    }
  })

  const validate = useCallback(() => {
    if (!name || !lastName || !email || !message) {
      setStatus('incomplete')
      return false
    }
    return true
  }, [name, lastName, email, message])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setStatus('loading')
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        body: JSON.stringify({ name, lastName, email, message }),
        headers: {
          'Content-Type': 'application/json'
        }
      })
      console.log(response.status)
      if (response.status !== 200) {
        throw new Error('Error', {
          cause: response.statusText
        })
      }
      setStatus('success')
    } catch (e) {
      console.error(e)
      setStatus('error')
    }
  }

  if (!show) return null

  return ReactDOM.createPortal(
    <div
      className="fixed w-full h-screen top-0 left-0 z-50 cursor-pointer"
      onClick={() => setIsMounted(false)}
    >
      <div
        className={`menu absolute right-0 h-full w-96 max-w-[calc(100vw-24px)] bg-black/60 overflow-auto no-scrollbar backdrop-blur-2xl transition-all duration-300 cursor-auto ${isMounted ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'} ${show ? 'show' : 'hide'}`}
      >
        <div
          className="w-full h-full flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex justify-end py-6 px-6">
            <button onClick={() => setIsMounted(false)}>
              <img src="/icons/close.svg" width={32} />
            </button>
          </div>
          <div className="px-6">
            <h3 className="font-serif text-3xl uppercase text-white">
              Contáctanos
            </h3>
            <form onSubmit={onSubmit}>
              <Input
                placeholder="Nombre"
                name="name"
                value={name}
                type="text"
                onChange={e =>
                  setForm(form => ({
                    ...form,
                    name: e.target.value
                  }))
                }
              />
              <Input
                placeholder="Apellidos"
                name="lastName"
                value={lastName}
                type="text"
                onChange={e =>
                  setForm(form => ({
                    ...form,
                    lastName: e.target.value
                  }))
                }
              />
              <Input
                placeholder="Email"
                name="email"
                value={email}
                type="email"
                onChange={e =>
                  setForm(form => ({
                    ...form,
                    email: e.target.value
                  }))
                }
              />
              <TextArea
                placeholder="Mensaje"
                name="message"
                value={message}
                onChange={e =>
                  setForm(form => ({
                    ...form,
                    message: e.target.value
                  }))
                }
              />
              <div className="flex justify-between items-start gap-4 mt-4">
                <Button
                  darkMode
                  noUpperCase
                  type="submit"
                  disabled={status === 'loading'}
                >
                  Enviar
                </Button>
                {status === 'success' && (
                  <Alert type="success">El mensaje se envió exitosamente</Alert>
                )}
                {status === 'error' && (
                  <Alert type="error">
                    Ocurrió un error al enviar el mensaje
                  </Alert>
                )}
                {status === 'incomplete' && (
                  <Alert type="error">Por favor, llena todos los campos</Alert>
                )}
              </div>
            </form>
          </div>
          <div className="flex flex-col flex-1 px-8 py-4 text-white justify-around">
            <div className="flex flex-row gap-6">
              <a
                href="https://www.instagram.com/distritoalamos_qro/"
                target="_blank"
                className="underline"
                rel="noreferrer"
              >
                Instagram
              </a>
            </div>
            <p className="font-bold text-xs">
              Conoce nuestro{' '}
              <Link to="/aviso-de-privacidad" className="underline">
                Aviso de Privacidad.
              </Link>
            </p>
          </div>
          <div className="flex flex-col flex-1 px-8 pt-4 pb-12 justify-end text-white gap-4">
            <p className="text-xs font-bold">
              Contáctenos para obtener más detalles.
            </p>
            <p className="text-xs">
              Al completar este formulario de registro, doy mi consentimiento
              expreso para: recibir mensajes electrónicos de 483 Distrito Álamos
              y sus empresas afiliadas de manera retroactiva, hoy y en el
              futuro, de 483 Distrito Álamos y sus comunidades.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
